import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Plus, Flame, Edit, Trash2, ChevronLeft, ChevronRight } from "lucide-react";

const MEAL_TYPES = {
  colazione: { label: "Colazione", color: "bg-yellow-100 text-yellow-700" },
  pranzo: { label: "Pranzo", color: "bg-orange-100 text-orange-700" },
  cena: { label: "Cena", color: "bg-purple-100 text-purple-700" },
  spuntino: { label: "Spuntino", color: "bg-green-100 text-green-700" }
};

export const NutritionDaySummary = ({ dailySummary, settings, calorieProgress }) => (
  <Card className="border-2 border-foreground shadow-sm rounded-sm">
    <CardHeader className="pb-2">
      <CardTitle className="font-heading text-base sm:text-lg flex items-center gap-2">
        <Flame className="w-5 h-5 text-orange-500" />
        Riepilogo Giornaliero
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">
      <div className="space-y-2">
        <div className="flex justify-between items-center text-sm">
          <span className="text-muted-foreground">Calorie</span>
          <span className="font-mono font-semibold">
            {dailySummary?.totals.calories.toFixed(0) || 0} / {dailySummary?.goal || settings.daily_calorie_goal}
          </span>
        </div>
        <Progress 
          value={Math.min(calorieProgress, 100)} 
          className={`h-2 ${calorieProgress > 100 ? '[&>div]:bg-red-500' : ''}`}
        />
      </div>
      <div className="grid grid-cols-3 gap-2 pt-2 border-t text-center">
        <div>
          <p className="text-[10px] text-muted-foreground">Proteine</p>
          <p className="font-mono text-sm font-semibold text-blue-600">{dailySummary?.totals.proteins.toFixed(0) || 0}g</p>
        </div>
        <div>
          <p className="text-[10px] text-muted-foreground">Carboidrati</p>
          <p className="font-mono text-sm font-semibold text-yellow-600">{dailySummary?.totals.carbs.toFixed(0) || 0}g</p>
        </div>
        <div>
          <p className="text-[10px] text-muted-foreground">Grassi</p>
          <p className="font-mono text-sm font-semibold text-red-600">{dailySummary?.totals.fats.toFixed(0) || 0}g</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

export const NutritionDateNav = ({ selectedDate, navigateDate, isToday }) => (
  <Card className="border border-border shadow-sm rounded-sm">
    <CardContent className="py-3">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigateDate(-1)}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="text-center">
          <span className="font-heading text-base sm:text-lg">
            {new Date(selectedDate).toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "long" })}
          </span>
          {isToday && <Badge variant="secondary" className="ml-2 text-xs">Oggi</Badge>}
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigateDate(1)}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </CardContent>
  </Card>
);

export const NutritionMealList = ({ meals, mealsByType, onAdd, onEdit, onDelete }) => (
  <div className="space-y-3">
    <h2 className="font-heading text-base sm:text-lg">Pasti di oggi</h2>
    {meals.length === 0 ? (
      <Card className="border border-dashed border-muted-foreground/30 rounded-sm">
        <CardContent className="py-6 text-center">
          <p className="text-muted-foreground text-sm mb-3">Nessun pasto registrato</p>
          <Button variant="outline" size="sm" onClick={onAdd} className="rounded-sm">
            <Plus className="w-4 h-4 mr-2" /> Aggiungi il primo pasto
          </Button>
        </CardContent>
      </Card>
    ) : (
      <div className="space-y-3">
        {Object.keys(MEAL_TYPES).map(mealType => {
          const mealConfig = MEAL_TYPES[mealType];
          const typeMeals = mealsByType[mealType] || [];
          if (typeMeals.length === 0) return null;
          return typeMeals.map(meal => (
            <Card key={meal.meal_id} className={`border rounded-sm ${mealConfig.color.split(' ')[0]}`}>
              <CardContent className="p-3">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-sm ${mealConfig.color}`}>
                    <span className="text-xs font-medium">{mealConfig.label.charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">{mealConfig.label}</span>
                      <span className="font-mono text-sm font-semibold">{meal.total_calories} kcal</span>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">{meal.description}</p>
                    <div className="flex gap-2 mt-1 text-[10px]">
                      <span className="text-blue-600">P: {meal.total_proteins}g</span>
                      <span className="text-yellow-600">C: {meal.total_carbs}g</span>
                      <span className="text-red-600">G: {meal.total_fats}g</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => onEdit(meal)} className="p-1.5 text-muted-foreground hover:text-foreground">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => onDelete(meal.meal_id)} className="p-1.5 text-muted-foreground hover:text-destructive">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ));
        })}
      </div>
    )}
  </div>
);
